import { useState } from 'react';
import { Shield, ShieldCheck, ShieldOff, Search } from 'lucide-react';
import { useAdminUsers } from '../../hooks/useAdminData';
import adminService from '../../services/admin.service';
import { useQueryClient } from '@tanstack/react-query';

export function GlobalAdminsAdmin() {
  const [search, setSearch] = useState('');
  const [toggling, setToggling] = useState<string | null>(null);
  const { data: usersData, isLoading } = useAdminUsers();
  const queryClient = useQueryClient();

  const users = usersData?.users ?? [];

  const filteredUsers = search
    ? users.filter(u =>
        u.userName?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const globalAdmins = filteredUsers.filter(u => u.roles.includes('GlobalAdmin'));
  const regularUsers = filteredUsers.filter(u => !u.roles.includes('GlobalAdmin'));

  const handleToggle = async (username: string) => {
    if (!username) return;
    setToggling(username);
    try {
      await adminService.toggleGlobalAdmin(username);
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    } catch (error) {
      console.error('Failed to toggle global admin:', error);
    } finally {
      setToggling(null);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Global Admins</h1>
        <p className="text-slate-600 mt-1">Administrer hvem som har GlobalAdmin-tilgang i systemet</p>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Søk brukere..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-slate-500">Laster brukere...</div>
      ) : (
        <>
          {/* Current Global Admins */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <ShieldCheck size={18} className="text-blue-600" />
              <h2 className="font-semibold text-slate-800">
                Global Admins ({globalAdmins.length})
              </h2>
            </div>
            {globalAdmins.length === 0 ? (
              <div className="p-6 text-center text-slate-500">Ingen global admins funnet</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {globalAdmins.map((user) => (
                  <div key={user.userName} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Shield size={18} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{user.userName}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex flex-wrap gap-1">
                        {user.roles.map(role => (
                          <span key={role} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                            {role}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={() => handleToggle(user.userName!)}
                        disabled={toggling === user.userName}
                        className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 px-3 py-1.5 rounded-lg hover:bg-red-50 disabled:opacity-50"
                      >
                        <ShieldOff size={14} />
                        {toggling === user.userName ? 'Endrer...' : 'Fjern'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Other Users */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <Shield size={18} className="text-slate-400" />
              <h2 className="font-semibold text-slate-800">
                Andre brukere ({regularUsers.length})
              </h2>
            </div>
            {regularUsers.length === 0 ? (
              <div className="p-6 text-center text-slate-500">Ingen brukere funnet</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {regularUsers.map((user) => (
                  <div key={user.userName} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                        <Shield size={18} className="text-slate-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{user.userName}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {user.roles.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {user.roles.map(role => (
                            <span key={role} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                              {role}
                            </span>
                          ))}
                        </div>
                      )}
                      <button
                        onClick={() => handleToggle(user.userName!)}
                        disabled={toggling === user.userName}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-50 disabled:opacity-50"
                      >
                        <ShieldCheck size={14} />
                        {toggling === user.userName ? 'Endrer...' : 'Gjør til admin'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default GlobalAdminsAdmin;
